<template>
  <div class="story-player" @click="handleClick">
    <!-- Background image or video -->
    <div :key="currentSceneIndex" class="bg-layer">
      <video
        v-if="currentScene && currentBgUrl && currentBgIsVideo"
        ref="bgVideo"
        class="bg-video"
        :style="{ opacity: bgOpacity }"
        :src="currentBgUrl"
        muted
        playsinline
        @ended="onBackgroundVideoEnded"
        @error="onBackgroundVideoError"
      />
      <div
        v-else-if="currentScene && currentBgUrl && !currentBgIsVideo"
        class="bg-image"
        :style="{
          backgroundImage: 'url(' + currentBgUrl + ')',
          opacity: bgOpacity
        }"
      />
      <div
        v-else
        class="bg-image"
        :style="{ backgroundImage: 'none', opacity: bgOpacity }"
      />
    </div>

    <!-- Subtitle（视频背景场景仅播视频，不叠台词） -->
    <div
      v-if="currentScene && !isEnded && !isTransitioning && !videoOnlyScene"
      class="subtitle-container"
    >
      <div class="subtitle-box">
        <div v-if="currentLineSpeakerLabel" class="subtitle-speaker">{{ currentLineSpeakerLabel }}</div>
        <div class="subtitle-text-wrapper">
          <div class="subtitle-text ghost-text">{{ currentLineText }}</div>
          <div class="subtitle-text visible-text">{{ displayedText }}</div>
        </div>
      </div>
    </div>

    <!-- Options Modal -->
    <div v-if="showOptions && currentScene" class="option-overlay" @click.stop>
      <div class="option-list">
        <button
          v-for="(option, idx) in currentScene.options"
          :key="idx"
          class="option-button"
          @click="selectOption(option)"
        >
          {{ option.text }}
        </button>
      </div>
    </div>

    <!-- Mini Game Options Modal -->
    <div v-if="showMiniGameOptions && currentScene" class="option-overlay" @click.stop>
      <div class="option-list">
        <button
          class="option-button"
          @click="selectMiniGameResult(true)"
        >
          成功 (通关)
        </button>
        <button
          class="option-button"
          @click="selectMiniGameResult(false)"
        >
          失败 (未通关)
        </button>
      </div>
    </div>

    <!-- End Screen -->
    <div v-if="isEnded" class="end-screen">
      <div>故事结束</div>
    </div>
  </div>
</template>

<script>
import { resolveMediaUrl } from '@/utils/media'
import { EMOTION_MAP } from '@/config/emotions'

export default {
  name: 'StoryPlayPlayer',

  props: {
    scenes: {
      type: Array,
      default: () => []
    },
    typingSpeed: {
      type: Number,
      default: 250
    },
    fadeDuration: {
      type: Number,
      default: 500
    },
    startSceneIndex: {
      type: Number,
      default: 0
    },
    continueSpace: {
      type: Number,
      default: -1
    }
  },

  data() {
    return {
      currentSceneIndex: 0,
      currentLineIndex: 0,
      displayedText: '',
      isTyping: false,
      typingTimer: null,
      bgOpacity: 1,
      showOptions: false,
      showMiniGameOptions: false,
      isEnded: false,
      isTransitioning: false,
      continueTimer: null,
      emotionMap: EMOTION_MAP
    }
  },

  computed: {
    currentScene() {
      if (this.scenes.length === 0) return null
      if (this.currentSceneIndex < 0 || this.currentSceneIndex >= this.scenes.length) return null
      return this.scenes[this.currentSceneIndex]
    },

    isMiniGameScene() {
      const s = this.currentScene
      return !!(s && (s.bg_type === 'minigame' || s.bg_type === 'third_party_game'))
    },

    currentLines() {
      if (!this.currentScene) return []
      return this.currentScene.lines || []
    },
    currentLineRaw() {
      if (this.currentLineIndex < 0 || this.currentLineIndex >= this.currentLines.length) {
        return null
      }
      return this.currentLines[this.currentLineIndex]
    },
    currentLineText() {
      if (this.isMiniGameScene) {
        return "玩家开始小游戏"
      }
      const line = this.currentLineRaw
      if (!line) return ""
      return typeof line === "string" ? line : (line.text || "")
    },
    currentLineSpeakerLabel() {
      if (this.isMiniGameScene) {
        return ""
      }
      const line = this.currentLineRaw
      if (!line || typeof line === "string") {
        return ""
      }
      const speaker = (line.speaker_name || "").trim()
      const emotionKey = (line.emotion || "").trim()
      const emotion = this.emotionMap[emotionKey] || emotionKey
      if (speaker && emotion) return `${speaker} - ${emotion}`
      return speaker || emotion || ""
    },

    currentBgUrl() {
      const s = this.currentScene
      if (!s || !s.bg_image) return ''
      return this.resolveMediaUrl(s.bg_image)
    },

    currentBgIsVideo() {
      const s = this.currentScene
      return !!(s && (s.bg_type || 'image') === 'video')
    },

    /** 当前为「只播视频」场景：有视频地址，不展示台词、不随点击切行 */
    videoOnlyScene() {
      return !!(this.currentBgIsVideo && this.currentBgUrl)
    }
  },

  watch: {
    scenes: {
      immediate: true,
      handler(newVal) {
        this.resetPlayer()
        if (newVal && newVal.length > 0) {
          const startIndex = Math.max(0, Math.min(this.startSceneIndex, newVal.length - 1))
          this.startScene(startIndex)
        }
      }
    }
  },

  beforeDestroy() {
    this.clearTypingTimer()
    this.clearContinueTimer()
  },

  methods: {
    resolveMediaUrl,
    resetPlayer() {
      this.clearTypingTimer()
      this.currentSceneIndex = 0
      this.currentLineIndex = 0
      this.displayedText = ''
      this.isTyping = false
      this.bgOpacity = 1
      this.showOptions = false
      this.showMiniGameOptions = false
      this.isEnded = false
      this.isTransitioning = false
      this.clearContinueTimer()
    },

    startScene(index, skipToEnd = false) {
      this.currentSceneIndex = index
      this.currentLineIndex = 0
      this.displayedText = ''
      this.isTyping = false
      this.showOptions = false
      this.showMiniGameOptions = false
      this.isTransitioning = false
      this.clearContinueTimer()
      this.clearTypingTimer()

      if (this.isMiniGameScene) {
        this.displayedText = '玩家开始小游戏'
        this.isTyping = false
        this.showMiniGameOptions = true
        return
      }

      if (this.videoOnlyScene) {
        if (skipToEnd) {
          this.handleSceneEnd()
          return
        }
        this.$nextTick(() => {
          this.tryPlayBackgroundVideo()
        })
        return
      }

      const lines = this.currentLines
      if (lines.length > 0) {
        if (skipToEnd) {
          this.currentLineIndex = lines.length - 1
          const line = lines[this.currentLineIndex]
          this.displayedText = typeof line === 'string' ? line : (line.text || '')
          this.isTyping = false
        } else {
          this.startTyping()
        }
      } else {
        this.handleSceneEnd()
      }
    },

    tryPlayBackgroundVideo() {
      const v = this.$refs.bgVideo
      if (!v) return
      try {
        v.currentTime = 0
      } catch (e) {
        /* ignore */
      }
      const p = v.play()
      if (p !== undefined && typeof p.catch === 'function') {
        p.catch(() => {})
      }
    },

    onBackgroundVideoEnded() {
      if (this.isTransitioning || this.isEnded || this.showOptions) return
      if (!this.videoOnlyScene) return
      this.handleSceneEnd()
    },

    onBackgroundVideoError() {
      if (this.isTransitioning || this.isEnded || this.showOptions) return
      if (!this.videoOnlyScene) return
      this.handleSceneEnd()
    },

    startTyping() {
      if (this.currentLineIndex >= this.currentLines.length) return
      const fullText = this.currentLineText
      this.displayedText = ''
      this.isTyping = true
      let charIndex = 0
      this.clearTypingTimer()
      this.typingTimer = setInterval(() => {
        if (charIndex < fullText.length) {
          this.displayedText += fullText.charAt(charIndex)
          charIndex++
        } else {
          this.clearTypingTimer()
          this.isTyping = false
          this.handleTypingFinished()
        }
      }, this.typingSpeed)
    },

    handleTypingFinished() {
      if (this.continueSpace !== -1) {
        this.clearContinueTimer()
        this.continueTimer = setTimeout(() => {
          this.continueTimer = null
          this.goToNext()
        }, this.continueSpace)
      }
    },

    clearContinueTimer() {
      if (this.continueTimer) {
        clearTimeout(this.continueTimer)
        this.continueTimer = null
      }
    },

    clearTypingTimer() {
      if (this.typingTimer) {
        clearInterval(this.typingTimer)
        this.typingTimer = null
      }
    },

    handleClick() {
      if (this.isTransitioning || this.showOptions || this.showMiniGameOptions || this.isEnded) return
      if (this.videoOnlyScene) return
      this.clearContinueTimer()
      if (this.isTyping) {
        this.clearTypingTimer()
        this.isTyping = false
        if (this.currentLineIndex < this.currentLines.length) {
          this.displayedText = this.currentLineText
          this.handleTypingFinished()
        }
      } else {
        this.goToNext()
      }
    },

    goToNext() {
      this.currentLineIndex++
      if (this.currentLineIndex < this.currentLines.length) {
        this.startTyping()
      } else {
        this.handleSceneEnd()
      }
    },

    handleSceneEnd() {
      const scene = this.currentScene
      if (!scene) return
      if (scene.options && scene.options.length > 0) {
        this.showOptions = true
      } else if (scene.next_scene_sort) {
        const targetIndex = this.findSceneIndexBySort(scene.next_scene_sort)
        const skipToEnd = !!scene.next_scene_skip_to_end
        this.transitionToScene(targetIndex, skipToEnd)
      } else {
        this.isEnded = true
      }
    },

    transitionToScene(targetIndex, skipToEnd = false) {
      if (targetIndex < 0 || targetIndex >= this.scenes.length) {
        this.isEnded = true
        return
      }
      if (targetIndex === this.currentSceneIndex) return
      this.isTransitioning = true
      this.showOptions = false
      this.bgOpacity = 0

      const nextScene = this.scenes[targetIndex]
      const mediaUrl = nextScene && nextScene.bg_image ? this.resolveMediaUrl(nextScene.bg_image) : null
      const isVideo = !!(nextScene && (nextScene.bg_type || 'image') === 'video')

      let hasProceeded = false
      const proceed = () => {
        if (hasProceeded) return
        hasProceeded = true
        this.startScene(targetIndex, skipToEnd)
        this.$nextTick(() => {
          this.bgOpacity = 1
          this.isTransitioning = false
        })
      }

      if (mediaUrl && !isVideo) {
        const img = new Image()
        const startTime = Date.now()

        const onReady = () => {
          const elapsed = Date.now() - startTime
          const delay = Math.max(0, this.fadeDuration - elapsed)
          setTimeout(proceed, delay)
        }

        img.onload = onReady
        img.onerror = onReady
        img.src = mediaUrl

        setTimeout(onReady, 3000)
      } else if (mediaUrl && isVideo) {
        const startTime = Date.now()
        const onReady = () => {
          const elapsed = Date.now() - startTime
          const delay = Math.max(0, this.fadeDuration - elapsed)
          setTimeout(proceed, delay)
        }
        const v = document.createElement('video')
        v.muted = true
        v.preload = 'auto'
        v.src = mediaUrl
        v.addEventListener('canplay', onReady, { once: true })
        v.addEventListener('error', onReady, { once: true })
        setTimeout(onReady, 5000)
      } else {
        setTimeout(proceed, this.fadeDuration)
      }
    },

    findSceneIndexBySort(sortOrder) {
      return this.scenes.findIndex(s => s.sort_order === sortOrder)
    },

    selectOption(option) {
      this.showOptions = false
      if (option.next_scene_sort !== undefined && option.next_scene_sort !== null) {
        const targetIndex = this.findSceneIndexBySort(option.next_scene_sort)
        if (targetIndex >= 0) {
          const skipToEnd = !!option.skip_to_end
          this.transitionToScene(targetIndex, skipToEnd)
        } else {
          this.isEnded = true
        }
      } else {
        this.isEnded = true
      }
    },

    selectMiniGameResult(success) {
      this.showMiniGameOptions = false
      const scene = this.currentScene
      if (!scene) return

      let targetSort = null
      let skipToEnd = false

      if (success) {
        targetSort = scene.next_scene_sort
        skipToEnd = !!scene.next_scene_skip_to_end
      } else {
        targetSort = scene.game_fail_next_scene_sort
        skipToEnd = !!scene.game_fail_next_scene_skip_to_end
      }

      if (targetSort !== undefined && targetSort !== null) {
        const targetIndex = this.findSceneIndexBySort(targetSort)
        if (targetIndex >= 0) {
          this.transitionToScene(targetIndex, skipToEnd)
        } else {
          this.isEnded = true
        }
      } else {
        this.isEnded = true
      }
    }
  }
}
</script>

<style scoped>
.story-player {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  cursor: pointer;
  background-color: #000;
}

.bg-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: opacity 0.5s ease;
}

.bg-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.5s ease;
}

.subtitle-container {
  position: absolute;
  bottom: 80px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  z-index: 10;
}

.subtitle-box {
  background-color: rgba(0, 0, 0, 0.7);
  padding: 16px 32px;
  border-radius: 8px;
  max-width: 80%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  align-items: center;
}

.subtitle-speaker {
  width: 100%;
  color: #ffd27d;
  font-size: 14px;
  line-height: 1.2;
}

.subtitle-text-wrapper {
  position: relative;
  width: 100%;
}

.subtitle-text {
  color: #fff;
  font-size: 18px;
  line-height: 1.6;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
}

.ghost-text {
  visibility: hidden;
  pointer-events: none;
}

.visible-text {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.option-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.option-button {
  background-color: rgba(30, 30, 50, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 14px 32px;
  border-radius: 8px;
  color: #fff;
  min-width: 240px;
  text-align: center;
  cursor: pointer;
  font-size: 16px;
}

.option-button:hover {
  background-color: rgba(50, 50, 70, 0.95);
  border-color: rgba(255, 255, 255, 0.4);
}

.end-screen {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  font-size: 24px;
  z-index: 30;
}
</style>
